import { Box, SxProps } from "@mui/material";
import FloatingPanel from "../common/FloatingPanel";
import NonGeoEntityCard from "../cards/NonGeoEntityCard";
import { SpatialDatasetDetail } from "../spatial-datasets";
import useEntityMapModalController from "../../hooks/useEntityMapModalController";
import type { Entity } from "../../types";
import { useUISelector } from "../../store/UI";
import { useSelectedEntity } from "../../hooks/useSelectedEntity";
import { usePopupActions } from "../../store/popupStore";

function CornerModalContent({ entity }: { entity: Entity }) {
  const { endpoint } = useSelectedEntity();
  const { closePopup } = usePopupActions();
  const { isMediaDrawerVisible } = useUISelector();

  if (!entity || !endpoint) return null;

  function handleToggle() {
    closePopup();
  }

  // Spatial datasets get their own detail card — it handles loading, status,
  // visibility toggle, download, retry, and delete internally.
  if (endpoint.entityType === "spatial-dataset") {
    return (
      <Box sx={{ mr: 5 }}>
        <SpatialDatasetDetail onClose={handleToggle} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        //@TODO: add adaptive height for different height scale
        maxHeight: isMediaDrawerVisible ? "314px" : "auto",
        padding: "1rem",
        bgcolor: "grey.800",
        transition: "height 300ms linear",
        transitionDelay: "1s",
        overflow: "hidden",
        "& #map-popup-thumbnail > img": {
          maxHeight: isMediaDrawerVisible ? "8.75rem!important" : "14.8rem",
          transition: "300ms ease-in-out"
        }
      }}
    >
      <NonGeoEntityCard entityEndpoint={endpoint} entity={entity} onClose={handleToggle} />
    </Box>
  );
}

export default function SelectedEntityCardCornerModal({ sx }: { sx?: SxProps }) {
  const { entity, endpoint } = useSelectedEntity();
  const { visibleContent, showModal } = useEntityMapModalController();

  // Spatial datasets have no collapsible full-screen modal — always render at the corner.
  const isSpatialDataset = endpoint?.entityType === "spatial-dataset";
  const isCorner = isSpatialDataset || !visibleContent || !showModal;

  if (!entity) return null;

  return (
    <Box
      sx={{
        display: "flex",
        position: "absolute",
        width: "100%",
        height: "100%",
        right: 0,
        alignItems: "center",
        justifyContent: "center",
        transition: "all 300ms linear",
        visibility: isCorner ? "visible" : "hidden",
        ...sx
      }}
    >
      <FloatingPanel moveContent={isCorner}>{isCorner && <CornerModalContent entity={entity} />}</FloatingPanel>
    </Box>
  );
}
